import {
    CCSRouteMap,
    type FRIIncreaseAmountType,
    type IBaseRentalContract,
    type IRentIncreaseConfig,
    type ITenantManagementProcess,
    type InvestorInfo,
    type Rent,
    type RentIncreaseFormula,
    type RentTenancy,
    type RentalContract,
    RentalContractRentIncreaseType,
    type StringDate,
    TenantManagementStates,
    resolveRoutePath,
} from '@condo/domain';
import dayjs from 'dayjs';
import { type Ref, type RendererElement, type RendererNode, type VNode, computed, h } from 'vue';

interface IBasePeriodicEntity {
    startDate?: StringDate;
    endDate?: StringDate;
}

const rentTenancyLink = (estateId: number, rentTenancyId?: number) => {
    return resolveRoutePath(CCSRouteMap['property-management.rent-tenancy'], {
        params: { estateId },
        query: { selectedRentTenancyId: rentTenancyId },
    });
};

const investorInfoLink = (estateId: number, investorInfoId?: number) => {
    return resolveRoutePath(CCSRouteMap['property-management.investor-data'], {
        params: { estateId },
        query: { selectedInvestorInfoId: investorInfoId },
    });
};

export const usePeriodicEntity = <TEntity extends IBasePeriodicEntity>(
    props: TEntity | undefined,
    pk: 'investorInfoId' | 'rentId' | 'rentTenancyId',
    changeActiveValues?: Ref<boolean>,
) => {
    const isCurrentlyActive = computed(() => {
        if (!props || !props[pk as keyof TEntity]) {
            return false;
        }
        const { startDate, endDate } = props;
        const now = dayjs();
        if (endDate) {
            return now.isAfter(startDate) && now.isBefore(endDate);
        }
        return now.isAfter(startDate);
    });

    const isInPast = computed(() => {
        if (!props || !props[pk as keyof TEntity]) {
            return false;
        }
        const { endDate } = props;
        const now = dayjs();
        if (endDate) {
            return now.isAfter(endDate);
        }
        return false;
    });

    // disable all fields except the end-date when an entity is active or in the past
    const shouldDisableFields = computed(() => props?.[pk as keyof TEntity] && !changeActiveValues?.value && (isCurrentlyActive.value || isInPast.value));

    return {
        isCurrentlyActive,
        isInPast,
        shouldDisableFields,
    };
};

export enum InvestorInfoProblemCode {
    AbsentEscrowAccount = 'AbsentEscrowAccount',
    AbsentInvestorBankAccount = 'AbsentInvestorBankAccount',
    AbsentActiveOrFutureTenancy = 'AbsentActiveOrFutureTenancy',
    EndedTenancyWithoutDepositReturn = 'EndedTenancyWithoutDepositReturn',
    AbsentRent = 'AbsentRent',
    AbsentActiveRent = 'AbsentActiveRent',
    NoFutureTenancies = 'NoFutureTenancies',
    NoInvestorInfo = 'NoInvestorInfo',
    NoInvestors = 'NoInvestors',
    NoFutureRent = 'NoFutureRent',
    IndexRentNoEarliestRentChangeDate = 'IndexRentNoEarliestRentChangeDate',
    PossibleExpiredIndexRent = 'PossibleExpiredIndexRent',
    EarliestRentChangeDateExpired = 'EarliestRentChangeDateExpired',
    RentIncreaseSoon = 'RentIncreaseSoon',
}

export const generalTenancyProblemCodes = [InvestorInfoProblemCode.AbsentActiveOrFutureTenancy];
export const tenancySpecificProblemCodes = [
    InvestorInfoProblemCode.EndedTenancyWithoutDepositReturn,
    InvestorInfoProblemCode.IndexRentNoEarliestRentChangeDate,
    InvestorInfoProblemCode.EarliestRentChangeDateExpired,
    InvestorInfoProblemCode.NoFutureTenancies,
    InvestorInfoProblemCode.NoFutureRent,
];
export const generalRentProblemCodes = [InvestorInfoProblemCode.AbsentActiveRent, InvestorInfoProblemCode.AbsentRent];
export const rentSpecificProblemCodes = [InvestorInfoProblemCode.PossibleExpiredIndexRent];
export interface InvestorInfoProblem {
    code: InvestorInfoProblemCode;
    investorInfoId?: number;
    rentTenancyId?: number;
    estateId: number;
    rentId?: number;
}

export interface InvestorInfoWarning {
    message: string;
    type: 'error' | 'warning' | 'info';
    html?: VNode<
        RendererNode,
        RendererElement,
        {
            [key: string]: any;
        }
    >;
    problem: InvestorInfoProblem;
}

const detectProblemsForInvestorTenancies = (rentTenancies: RentTenancy[], estateId: number): InvestorInfoProblem[] => {
    const problems: InvestorInfoProblem[] = [];

    if (!rentTenancies?.length) {
        return problems;
    }

    const endedTenancies = rentTenancies.filter(rt => rt.endDate && dayjs(rt.endDate).isBefore(dayjs()));

    endedTenancies.forEach(rt => {
        const latestRentalContract = rt.latestRentalContract;
        if (!latestRentalContract?.rentDeposit || !latestRentalContract?.paidRentDeposit) {
            problems.push({ code: InvestorInfoProblemCode.EndedTenancyWithoutDepositReturn, estateId, rentTenancyId: rt.rentTenancyId });
        }
    });

    /**
     * from here on we only check active or future tenancies
     */
    // if (!rentTenancies.some(t => t.isActive || t.isInFuture)) {
    //     problems.push({ code: InvestorInfoProblemCode.AbsentActiveOrFutureTenancy, investorInfoId, estateId });
    //     return problems;
    // }

    /**
     * Check all active tenancies
     */

    const activeTenancies = rentTenancies.filter(rt => rt.isActive);

    if (!activeTenancies.length) {
        return problems;
    }

    for (const activeTenancy of activeTenancies) {
        const isIndex = activeTenancy.latestRentalContract?.rentIncreaseType === RentalContractRentIncreaseType.IRI;
        const rentTenancyId = activeTenancy.rentTenancyId;
        const hasFutureTenancies = rentTenancies.some(rt => rt.isInFuture);
        const rentalContract = activeTenancy.latestRentalContract;
        if (!rentalContract) {
            throw new Error('Rental contract is not set for tenancy');
        }
        const earliestRentChangeDate = dayjs(rentalContract.nextRentIncreaseDate);
        const isPastEarliestRentChange = earliestRentChangeDate.isBefore(dayjs());

        if (activeTenancy.endDate && !hasFutureTenancies) {
            problems.push({ code: InvestorInfoProblemCode.NoFutureTenancies, estateId, rentTenancyId });
        }

        if (isIndex && isPastEarliestRentChange) {
            problems.push({
                code: InvestorInfoProblemCode.EarliestRentChangeDateExpired,
                estateId,
                rentTenancyId,
            });
        }

        if (isIndex && !rentalContract.nextRentIncreaseDate) {
            problems.push({ code: InvestorInfoProblemCode.IndexRentNoEarliestRentChangeDate, rentTenancyId, estateId });
        }

        /**
         * check rents
         * we also only check for problems in active rents for now
         */

        if (!activeTenancy.rents?.length) {
            problems.push({ code: InvestorInfoProblemCode.AbsentRent, estateId, rentTenancyId });
            continue; // Skip to next tenancy
        }

        // there should be only 1 active rent per tenancy
        const activeRent = activeTenancy.rents?.filter(t => t?.isActive)[0];
        const activeRentStartDate = activeRent?.startDate ? dayjs(activeRent.startDate) : null;
        const activeRentEndDate = activeRent?.endDate ? dayjs(activeRent.endDate) : null;

        if (activeTenancy && !activeRent) {
            problems.push({ code: InvestorInfoProblemCode.AbsentActiveRent, estateId, rentTenancyId });
        }

        /**
         * note: if (!rentStartDate.isBefore(earliestRentChangeDate) && isIndex && isPastEarliestRentChange)
         * then the only thing wrong is that earliestRentChangeDate is expired
         * because it means a new rent was added after the earliestRentChangeDate
         */
        if (activeRentStartDate?.isBefore(earliestRentChangeDate) && isIndex && isPastEarliestRentChange) {
            problems.push({
                code: InvestorInfoProblemCode.PossibleExpiredIndexRent,
                estateId,
                rentTenancyId,
                rentId: activeRent.rentId,
            });
        }

        const hasFutureRents = activeTenancy.rents?.filter(t => t?.isActive).length;

        if (activeRentEndDate && !hasFutureRents && !hasFutureTenancies) {
            problems.push({
                code: InvestorInfoProblemCode.NoFutureRent,
                estateId,
                rentTenancyId,
                rentId: activeRent.rentId,
            });
        }
    }

    return problems;
};

const detectProblemsForInvestorInfo = (investorInfo: InvestorInfo): InvestorInfoProblem[] => {
    const investorInfoProblems: InvestorInfoProblem[] = [];

    const investorInfoId = investorInfo?.investorInfoId;

    /**
     * investor info problems
     */
    if (!investorInfoId) {
        investorInfoProblems.push({ code: InvestorInfoProblemCode.NoInvestorInfo, estateId: investorInfo?.estateId });
        return investorInfoProblems;
    }

    if (!investorInfo?.investorBankAccountId) {
        investorInfoProblems.push({ code: InvestorInfoProblemCode.AbsentInvestorBankAccount, investorInfoId, estateId: investorInfo?.estateId });
    }

    if (!investorInfo?.investors?.length) {
        investorInfoProblems.push({ code: InvestorInfoProblemCode.NoInvestors, investorInfoId, estateId: investorInfo?.estateId });
    }

    if (!investorInfo?.escrowBankAccountId) {
        investorInfoProblems.push({ code: InvestorInfoProblemCode.AbsentEscrowAccount, investorInfoId, estateId: investorInfo?.estateId });
    }

    const investorTenanciesProblems = detectProblemsForInvestorTenancies(investorInfo?.rentTenancies, investorInfo?.estateId);

    return investorInfoProblems.concat(investorTenanciesProblems);
};

const getWarningMessages = (problems: InvestorInfoProblem[]): InvestorInfoWarning[] => {
    return problems.map(problem => {
        switch (problem.code) {
            case InvestorInfoProblemCode.AbsentEscrowAccount:
                return {
                    message: `Investor Info ${problem.investorInfoId} does not have an ESCROW Account set. Please add it.`,
                    html: h(
                        'a',
                        { href: `${investorInfoLink(problem.estateId, problem.rentTenancyId)}` },
                        `Investor Info ${problem.investorInfoId} does not have an ESCROW Account set. Please set it.`,
                    ),
                    type: 'error',
                    problem,
                };
            case InvestorInfoProblemCode.AbsentInvestorBankAccount:
                return {
                    message: `Investor Info ${problem.investorInfoId} does not have an Investor Bank Account set. Please set it.`,
                    html: h(
                        'a',
                        { href: `${investorInfoLink(problem.estateId, problem.rentTenancyId)}` },
                        `Investor Info ${problem.investorInfoId} does not have an Investor Bank Account set. Please set it.`,
                    ),
                    type: 'warning',
                    problem,
                };
            case InvestorInfoProblemCode.AbsentActiveOrFutureTenancy:
                return {
                    message: `Estate does not have any active or future Tenancy.`,
                    html: h('a', { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` }, `Estate does not have any active or future Tenancy.`),
                    type: 'info',
                    problem,
                };
            case InvestorInfoProblemCode.AbsentRent:
                return {
                    message: `Missing rent for Tenancy ${problem.rentTenancyId}. Please add it.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Missing rent for Tenancy ${problem.rentTenancyId}. Please add it.`,
                    ),
                    type: 'warning',
                    problem,
                };
            case InvestorInfoProblemCode.AbsentActiveRent:
                return {
                    message: `Missing active rent for active Tenancy ${problem.rentTenancyId}. Please add it.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Missing active rent for active Tenancy ${problem.rentTenancyId}. Please add it.`,
                    ),
                    type: 'warning',
                    problem,
                };
            case InvestorInfoProblemCode.NoFutureRent:
                return {
                    message: `Current Tenancy ${problem.rentTenancyId} / Rent ${problem.rentId} is expiring soon and no new rents are added.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Current Tenancy ${problem.rentTenancyId} / Rent ${problem.rentId} is expiring soon and no new rents are added.`,
                    ),
                    type: 'info',
                    problem,
                };
            case InvestorInfoProblemCode.RentIncreaseSoon:
                return {
                    message: `Rent ${problem.rentId} is increasing soon. Please add a new rent.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Rent ${problem.rentId} is expiring soon. Please add a new rent.`,
                    ),
                    type: 'info',
                    problem,
                };
            case InvestorInfoProblemCode.NoFutureTenancies:
                return {
                    message: `Current Tenancy ${problem.rentTenancyId} is expiring soon and no new tenancies are added.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Current Tenancy ${problem.rentTenancyId} is expiring soon and no new tenancies are added.`,
                    ),
                    type: 'info',
                    problem,
                };
            case InvestorInfoProblemCode.NoInvestorInfo:
                return {
                    message: `This estate does not have an Investor Info. Please add it.`,
                    html: h('a', { href: `${investorInfoLink(problem.estateId)}` }, `This estate does not have an Investor Info. Please add it.`),
                    type: 'warning',
                    problem,
                };
            case InvestorInfoProblemCode.NoInvestors:
                return {
                    message: `Investor Info ${problem.investorInfoId} does not have any investors. Please add it.`,
                    html: h(
                        'a',
                        { href: `${investorInfoLink(problem.estateId)}` },
                        `Investor Info ${problem.investorInfoId} does not have any investors. Please add it.`,
                    ),
                    type: 'warning',
                    problem,
                };
            case InvestorInfoProblemCode.IndexRentNoEarliestRentChangeDate:
                return {
                    message: `Next Index Rent Increase is not set for the current Tenancy ${problem.rentTenancyId}. Please add it.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Next Index Rent Increase is not set for the current Tenancy ${problem.rentTenancyId}. Please add it.`,
                    ),
                    type: 'warning',
                    problem,
                };
            case InvestorInfoProblemCode.EarliestRentChangeDateExpired:
                return {
                    message: `Rent Increase Date is expired for Tenancy ${problem.rentTenancyId}. Please update it.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Rent Increase Date is expired for Tenancy ${problem.rentTenancyId}. Please update it.`,
                    ),
                    type: 'warning',
                    problem,
                };
            case InvestorInfoProblemCode.PossibleExpiredIndexRent:
                return {
                    message: `Rent ${problem.rentId} price is possibly outdated.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Rent ${problem.rentId} price is possibly outdated. Please add a new Rent for Tenancy ${problem.rentTenancyId}.`,
                    ),
                    type: 'warning',
                    problem,
                };
            case InvestorInfoProblemCode.EndedTenancyWithoutDepositReturn:
                return {
                    message: `Tenancy ${problem.rentTenancyId} has ended but rent deposit was not returned.`,
                    html: h(
                        'a',
                        { href: `${rentTenancyLink(problem.estateId, problem.rentTenancyId)}` },
                        `Tenancy ${problem.rentTenancyId} has ended but rent deposit was not returned.`,
                    ),
                    type: 'info',
                    problem,
                };
            default:
                return { message: 'Unknown problem', type: 'error', problem };
        }
    });
};
export const getActiveOrMostRecentRent = (tenancy: RentTenancy) => {
    return tenancy?.latestRentalContract?.activeOrMostRecentRent;
};

export const getProblemsMessagesFromInvestorInfo = (investorInfo: InvestorInfo): InvestorInfoWarning[] =>
    getWarningMessages(detectProblemsForInvestorInfo(investorInfo));

export const getProblemsMessagesFromInvestorTenancies = (investorTeanancies: RentTenancy[], estateId: number): InvestorInfoWarning[] =>
    getWarningMessages(detectProblemsForInvestorTenancies(investorTeanancies, estateId));

export const getRentOrTenancyStatus = (
    rentOrTenancy?: RentTenancy | Rent | null,
): { text: string; tagType: 'default' | 'error' | 'primary' | 'info' | 'success' | 'warning' } => ({
    text: rentOrTenancy?.isActive ? 'Active' : rentOrTenancy?.isInFuture ? 'Future' : rentOrTenancy?.endDate ? 'Expired' : '',
    tagType: rentOrTenancy?.isActive ? 'success' : rentOrTenancy?.isInFuture ? 'info' : 'warning',
});

export const getRentalContractStatus = (
    rentalContract?: RentalContract | null,
): { text: string; tagType: 'default' | 'error' | 'primary' | 'info' | 'success' | 'warning' } => ({
    text: rentalContract?.isActive ? 'Active' : rentalContract?.isInFuture ? 'Future' : rentalContract?.endDate ? 'Expired' : '',
    tagType: rentalContract?.isActive ? 'success' : rentalContract?.isInFuture ? 'info' : 'warning',
});

const tenantManagementProcessStateTagColors: Record<TenantManagementStates, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
    [TenantManagementStates.Initiated]: 'default',
    [TenantManagementStates.Onboarding]: 'primary',
    [TenantManagementStates.Active]: 'success',
    [TenantManagementStates.RentReduction]: 'warning',
    [TenantManagementStates.Cancelled]: 'error',
    [TenantManagementStates.TerminationNoticeReceived]: 'warning',
    [TenantManagementStates.TerminationAccepted]: 'primary',
    [TenantManagementStates.TerminationInitiated]: 'warning',
    [TenantManagementStates.HandoverScheduled]: 'primary',
    [TenantManagementStates.HandoverDone]: 'success',
    [TenantManagementStates.Terminated]: 'error',
    [TenantManagementStates.Closed]: 'error',
};

export const getTenantManagementStatus = (tenancy?: {
    tenantManagementProcess?: ITenantManagementProcess | null;
}): {
    text: string;
    tagType: 'default' | 'error' | 'primary' | 'info' | 'success' | 'warning';
} => ({
    text: (tenancy?.tenantManagementProcess?.processState?.state as TenantManagementStates) ?? 'Unknown',
    tagType: tenantManagementProcessStateTagColors[tenancy?.tenantManagementProcess?.processState?.state as TenantManagementStates] ?? 'default',
});

export function extractRentIncreaseConfig(val: IBaseRentalContract): IRentIncreaseConfig {
    return {
        rentIncreaseType: (val.rentIncreaseType ?? null) as RentalContractRentIncreaseType | null,
        rentIncreaseInterval: val.rentIncreaseInterval ?? null,
        rentIncreaseFormula: (val.rentIncreaseFormula ?? null) as RentIncreaseFormula | null,
        iriMinCap: val.iriMinCap ?? null,
        iriMaxCap: val.iriMaxCap ?? null,
        friIncreaseCycles: val.friIncreaseCycles ?? null,
        friIncreaseAmount: val.friIncreaseAmount ?? null,
        friIncreaseAmountType: (val.friIncreaseAmountType ?? null) as FRIIncreaseAmountType | null,
    };
}
